import React, { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import Popup from "../Popup/Popup";
import { getCurateResourceTypeLabel } from "../../../Helpers/fileIcon";
import {
  getPreviewKind,
  previewMimeType,
  previewNeedsBytes,
  previewOpensNatively,
  sniffOfficeKind,
} from "./previewKind";
import { openRenderedPreviewTab } from "./openRenderedPreviewTab";
import PreviewErrorBoundary from "./PreviewErrorBoundary";
import styles from "./DocumentViewer.module.css";

const DocxRenderer = lazy(() => import("./renderers/DocxRenderer"));
const SheetRenderer = lazy(() => import("./renderers/SheetRenderer"));
const PptxRenderer = lazy(() => import("./renderers/PptxRenderer"));
const TextRenderer = lazy(() => import("./renderers/TextRenderer"));
const UnsupportedFallback = lazy(() => import("./renderers/UnsupportedFallback"));

export type DocumentViewerProps = {
  fileName: string;
  extension?: string;
  url?: string | null;
  fetchBytes?: () => Promise<ArrayBuffer>;
  onClose: () => void;
  isVisible?: boolean;
  zIndex?: number;
  loading?: boolean;
  error?: string | null;
};

const triggerDownload = (url: string, fileName: string) => {
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  a.rel = "noopener noreferrer";
  document.body.appendChild(a);
  a.click();
  a.remove();
};

const DocumentViewer = ({
  fileName,
  extension,
  url,
  fetchBytes,
  onClose,
  isVisible = true,
  zIndex,
  loading = false,
  error = null,
}: DocumentViewerProps) => {
  const fileKey = `${extension ?? ""}\n${fileName}`;
  const kind = getPreviewKind(extension, fileName);
  const needsBytes = previewNeedsBytes(kind);
  const nativeFromBytes = previewOpensNatively(kind) && !url;
  const loadForPreview = needsBytes || nativeFromBytes;
  const [loaded, setLoaded] = useState<{ key: string; bytes: ArrayBuffer } | null>(null);
  const [objectMedia, setObjectMedia] = useState<{ key: string; url: string } | null>(null);
  const [bytesError, setBytesError] = useState<string | null>(null);
  const [bytesLoading, setBytesLoading] = useState(false);
  const previewRef = useRef<HTMLDivElement | null>(null);
  const currentBytes = loaded?.key === fileKey ? loaded.bytes : null;
  const objectUrl = objectMedia?.key === fileKey ? objectMedia.url : null;
  const previewUrl = url || objectUrl;
  const sniffed = useMemo(
    () => (currentBytes && needsBytes ? sniffOfficeKind(currentBytes) : null),
    [currentBytes, needsBytes]
  );
  const renderKind = sniffed ?? kind;

  useEffect(() => {
    setLoaded(null);
    setBytesError(null);
    if (!loadForPreview || !isVisible) {
      setBytesLoading(false);
      return;
    }

    if (!fetchBytes) {
      setBytesError("Unable to load this file.");
      setBytesLoading(false);
      return;
    }

    let cancelled = false;
    setBytesLoading(true);
    (async () => {
      try {
        const buffer = await fetchBytes();
        if (!cancelled) {
          setLoaded({ key: fileKey, bytes: buffer });
        }
      } catch {
        if (!cancelled) {
          setBytesError("Unable to load this file.");
        }
      } finally {
        if (!cancelled) {
          setBytesLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [fetchBytes, fileKey, isVisible, loadForPreview]);

  useEffect(() => {
    if (!nativeFromBytes || !currentBytes) {
      setObjectMedia(null);
      return;
    }

    const blob = new Blob([currentBytes], {
      type: previewMimeType(kind, extension, fileName),
    });
    const nextUrl = URL.createObjectURL(blob);
    setObjectMedia({ key: fileKey, url: nextUrl });
    return () => {
      URL.revokeObjectURL(nextUrl);
    };
  }, [currentBytes, extension, fileKey, fileName, kind, nativeFromBytes]);

  const loadBytes = useCallback(async () => {
    if (currentBytes) {
      return currentBytes;
    }
    if (fetchBytes) {
      return fetchBytes();
    }
    return null;
  }, [currentBytes, fetchBytes]);

  const handleDownload = useCallback(async () => {
    const buffer = await loadBytes();
    if (buffer) {
      const blob = new Blob([buffer], {
        type: previewMimeType(renderKind, extension, fileName),
      });
      const objectUrl = URL.createObjectURL(blob);
      triggerDownload(objectUrl, fileName);
      URL.revokeObjectURL(objectUrl);
      return;
    }

    if (url) {
      triggerDownload(url, fileName);
    }
  }, [extension, fileName, loadBytes, renderKind, url]);

  const handleOpenTab = useCallback(() => {
    if (previewOpensNatively(kind) && previewUrl) {
      window.open(previewUrl, "_blank", "noopener,noreferrer");
      return;
    }

    const html = previewRef.current?.innerHTML?.trim();
    if (html) {
      openRenderedPreviewTab(fileName, html);
    }
  }, [fileName, kind, previewUrl]);

  const typeLabel = getCurateResourceTypeLabel({
    title: fileName,
    extension,
  });

  const message = error || bytesError;
  const waitingForBytes = loadForPreview && isVisible && !currentBytes && !message;
  const waitingForObjectUrl = nativeFromBytes && !!currentBytes && !objectUrl && !message;
  const busy = loading || bytesLoading || waitingForBytes || waitingForObjectUrl;

  return (
    <Popup
      isVisible={isVisible}
      onClose={onClose}
      variant="document"
      title={fileName}
      zIndex={zIndex}
    >
      <div className={styles.container}>
        <div className={styles.toolbar}>
          <div className={styles.toolbarMeta}>
            <span className={styles.typeLabel}>{typeLabel}</span>
          </div>
          <div className={styles.toolbarActions}>
            <button
              type="button"
              className={styles.toolbarButton}
              onClick={handleDownload}
              disabled={!url && !fetchBytes && !currentBytes}
            >
              Download
            </button>
            <button
              type="button"
              className={styles.toolbarButton}
              onClick={handleOpenTab}
              disabled={
                !(previewOpensNatively(kind) && previewUrl) &&
                !(needsBytes && currentBytes && !busy && !message)
              }
            >
              Open in new tab
            </button>
          </div>
        </div>
        <div className={styles.body} ref={previewRef}>
          {busy && <p className={styles.status}>Loading…</p>}
          {!busy && message && <p className={styles.error}>{message}</p>}
          {!busy && !message && kind === "pdf" && previewUrl && (
            <iframe className={styles.frame} title={fileName} src={previewUrl} />
          )}
          {!busy && !message && kind === "image" && previewUrl && (
            <img className={styles.media} src={previewUrl} alt={fileName} />
          )}
          {!busy && !message && kind === "video" && previewUrl && (
            <video className={styles.media} src={previewUrl} controls />
          )}
          {!busy && !message && kind === "audio" && previewUrl && (
            <div className={styles.audioWrap}>
              <audio className={styles.audio} src={previewUrl} controls />
            </div>
          )}
          {!busy && !message && previewNeedsBytes(renderKind) && currentBytes && (
            <PreviewErrorBoundary resetKey={fileKey} fileName={fileName} onDownload={handleDownload}>
              <Suspense fallback={<p className={styles.status}>Preparing preview…</p>}>
                {renderKind === "docx" && <DocxRenderer bytes={currentBytes} />}
                {renderKind === "sheet" && <SheetRenderer bytes={currentBytes} />}
                {renderKind === "pptx" && <PptxRenderer bytes={currentBytes} />}
                {renderKind === "text" && <TextRenderer bytes={currentBytes} fileName={fileName} />}
              </Suspense>
            </PreviewErrorBoundary>
          )}
          {!busy && !message && kind === "unsupported" && (
            <Suspense fallback={<p className={styles.status}>Loading…</p>}>
              <UnsupportedFallback fileName={fileName} onDownload={handleDownload} />
            </Suspense>
          )}
          {!busy && !message && !needsBytes && kind !== "unsupported" && !previewUrl && (
            <p className={styles.error}>Unable to open this resource.</p>
          )}
        </div>
      </div>
    </Popup>
  );
};

export default DocumentViewer;
